import { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  Space,
  Card,
  Tag,
  Button,
  Tooltip,
  Checkbox,
  message,
  Switch,
} from "antd";
import {
  RobotOutlined,
  CheckOutlined,
  InfoCircleOutlined,
  BranchesOutlined,
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../../hooks/reduxHooks";
import {
  getBusinessInfoApi,
  getTenantLanguageListApi,
  getTenantToolsApi,
  getTenantPresetDataApi,
} from "../../../../services/tenantSetup.service";
import { requiredInfoSuggestions } from "../../../../data/requiredInfoSuggestions";
import { getStepData, updateStepData } from "../../../../utils/tenantSetupStorage";
import UpgradeDialog from "../../../../components/common/UpgradeDialog";

const { Title, Text, Paragraph } = Typography;
const { Option, OptGroup } = Select;
const { TextArea } = Input;

const Step2AIAgentSetup = ({ initialData, onSubmit, loading, orgType }) => {
  const [form] = Form.useForm();

  const [messageApi, contextHolder] = message.useMessage();

  const [languages, setLanguages] = useState([]);
  const [toolsList, setToolsList] = useState([]);
  const [primaryLanguage, setPrimaryLanguage] = useState(null);
  const [agentGoalTypes, setAgentGoalTypes] = useState([]);
  const [responseStyles, setResponseStyles] = useState([]);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const sessionDataLoadedRef = useRef(false);
  const dispatch = useAppDispatch();
  const { isPaidPlan } = useAppSelector((state) => state.currentPlan);

  const getCanonicalToolKey = (toolName) => {
    const tool = toolsList.find((item) =>
      item.issue_type === toolName || item.name === toolName || item.label === toolName
    );

    return tool ? tool.issue_type || tool.name : toolName;
  };

  const validateToolReplyMessages = (values) => {
    const selectedTools = values.intervention_tools || [];
    const fieldErrors = [];

    selectedTools.forEach((toolName) => {
      const toolKey = getCanonicalToolKey(toolName);
      const allowEmptyResponse = form.getFieldValue(`tool_allow_empty_response_${toolKey}`) ?? false;
      const replyMessage = form.getFieldValue(`tool_reply_message_${toolKey}`);

      if (!allowEmptyResponse && !replyMessage?.trim()) {
        fieldErrors.push({
          name: `tool_reply_message_${toolKey}`,
          errors: ["Reply message is required when empty responses are not allowed"],
        });
      }
    });

    if (fieldErrors.length > 0) {
      form.setFields(fieldErrors);
      form.scrollToField(fieldErrors[0].name);
      messageApi.error("Please add reply messages for follow-up tools that do not allow empty responses");
      return false;
    }

    return true;
  };

  useEffect(() => {
    getTenantPresetDataApi({
      finalCallback: () => { },
      successCallback: (response) => {
        setAgentGoalTypes(response.agent_goal_types || []);
        setResponseStyles(response.response_styles || []);
      },
      failureCallback: () => { },
    })();

    getTenantLanguageListApi({
      finalCallback: () => { },
      successCallback: (response) => {
        setLanguages(response);
      },
      failureCallback: () => { },
    })();

    getTenantToolsApi({
      finalCallback: () => { },
      successCallback: (response) => {
        // Response is always an array of tools
        const tools = Array.isArray(response) ? response : [];
        setToolsList(tools);
      },
      failureCallback: () => { },
    })();
  }, []);

  useEffect(() => {
    dispatch(
      getBusinessInfoApi({
        finalCallback: () => { },
        successCallback: (response) => {
          // Always populate from business info API (prioritize API over session data)
          let formData = {
            agent_name: response.agent_name || "AI Bot",
            language: response.language_to_reply || ["Auto Detect"],
            romanize_reply: response.romanize_reply || false,
            agent_goal_type: response.agent_goal_type || "",
            additional_agent_goal: response.additional_agent_goal || "",
            selectedResponseStyle: response.response_style || "",
            intervention_tools: response.selected_tools || [],
          };

          form.setFieldsValue(formData);
          sessionDataLoadedRef.current = true;
        },
        failureCallback: () => { },
      })
    );
  }, [dispatch, form]);

  // Normalize any legacy selected values (labels like "Ticket") to issue_type (e.g., "ticket")
  useEffect(() => {
    const allTools = toolsList || [];
    if (!allTools.length) return;
    const byLabel = new Map(allTools.map(t => [t.label, t.issue_type || t.name]));
    const validKeys = new Set(allTools.map(t => t.issue_type || t.name));
    const current = form.getFieldValue('intervention_tools') || [];
    if (!current.length) return;
    const normalized = current.map(v => {
      if (validKeys.has(v)) return v;
      return byLabel.get(v) || v;
    });
    if (JSON.stringify(normalized) !== JSON.stringify(current)) {
      form.setFieldValue('intervention_tools', normalized);
    }
  }, [toolsList, form]);

  // Migrate dynamic required-info fields from label-based keys to issue_type-based keys
  useEffect(() => {
    const allTools = toolsList || [];
    if (!allTools.length) return;
    const byLabel = new Map(allTools.map(t => [t.label, t.issue_type || t.name]));
    const values = form.getFieldsValue();
    let changed = false;
    Object.keys(values)
      .filter((k) => k.startsWith('tool_required_info_'))
      .forEach((k) => {
        const suffix = k.replace('tool_required_info_', '');
        const targetKey = byLabel.get(suffix);
        if (targetKey && !values[`tool_required_info_${targetKey}`]) {
          form.setFieldValue(`tool_required_info_${targetKey}`, values[k]);
          changed = true;
        }
      });
    // Optionally clear old label-based keys in UI; safe to leave as-is since they won't be referenced
    if (changed) {
      // trigger re-render
      form.setFieldsValue({});
    }
  }, [toolsList, form]);



  useEffect(() => {
    if (sessionDataLoadedRef.current) return;
    if (initialData) {
      let formInitialValues = {
        agent_name: "AI Bot",
        language: ["Auto Detect"],
        ...initialData,
      };

      // Combine legacy used_tools + booking into intervention_tools if not provided
      if (!initialData.intervention_tools) {
        const legacyTicket = Array.isArray(initialData.used_tools) ? initialData.used_tools : [];
        const legacyBooking = Array.isArray(initialData.booking) ? initialData.booking : [];
        formInitialValues.intervention_tools = Array.from(new Set([
          ...legacyTicket,
          ...legacyBooking,
        ]));
      }

      // Convert selected_tools_required_info back to dynamic form fields if present
      if (initialData.selected_tools_required_info && Array.isArray(initialData.selected_tools_required_info)) {
        const suggestionLabelToName = new Map((requiredInfoSuggestions || []).map(s => [s.label, s.name]));
        initialData.selected_tools_required_info.forEach(toolInfo => {
          const key = `tool_required_info_${toolInfo.tool}`;
          formInitialValues[key] = (toolInfo.required_info || []).map(i => suggestionLabelToName.get(i) || i);
          formInitialValues[`tool_reply_message_${toolInfo.tool}`] = toolInfo.reply_message || "";
          formInitialValues[`tool_allow_empty_response_${toolInfo.tool}`] = toolInfo.allow_empty_response || false;
          formInitialValues[`tool_block_reply_until_resolved_${toolInfo.tool}`] = toolInfo.block_reply_until_resolved || false;
        });
      }

      form.setFieldsValue(formInitialValues);

      // Set primary language (first language in the array is considered primary)
      const languageArray = initialData.language || [];
      if (languageArray.length > 0 && languageArray[0] !== "Auto Detect") {
        setPrimaryLanguage(languageArray[0]);
      }
    }
  }, [initialData, form]);

  // Load step data from session storage on mount (only if no business info loaded)
  useEffect(() => {
    const savedStepData = getStepData("step2");
    if (savedStepData && Object.keys(savedStepData).length > 0 && !sessionDataLoadedRef.current) {
      let formValues = {
        agent_name: savedStepData.agent_name || "AI Bot",
        language: savedStepData.language || ["Auto Detect"],
        romanize_reply: savedStepData.romanize_reply || false,
        agent_goal_type: savedStepData.agent_goal_type || "",
        additional_agent_goal: savedStepData.additional_agent_goal || "",
        prompt: savedStepData.prompt || "standard",
        intervention_tools: savedStepData.intervention_tools || [],
        ...savedStepData,
      };

      if (Array.isArray(savedStepData.selected_tools_required_info)) {
        const suggestionLabelToName = new Map((requiredInfoSuggestions || []).map(s => [s.label, s.name]));
        savedStepData.selected_tools_required_info.forEach((toolInfo) => {
          const key = `tool_required_info_${toolInfo.tool}`;
          formValues[key] = (toolInfo.required_info || []).map(i => suggestionLabelToName.get(i) || i);
          formValues[`tool_reply_message_${toolInfo.tool}`] = toolInfo.reply_message || "";
          formValues[`tool_allow_empty_response_${toolInfo.tool}`] = toolInfo.allow_empty_response || false;
          formValues[`tool_block_reply_until_resolved_${toolInfo.tool}`] = toolInfo.block_reply_until_resolved || false;
        });
      }

      form.setFieldsValue(formValues);

      // Set primary language
      const languageArray = savedStepData.language || [];
      if (languageArray.length > 0 && languageArray[0] !== "Auto Detect") {
        setPrimaryLanguage(languageArray[0]);
      }
      sessionDataLoadedRef.current = true;
    }
  }, [form]);

  useEffect(() => {
    // Load existing business info for agent setup
    dispatch(
      getBusinessInfoApi({
        finalCallback: () => { },
        successCallback: (response) => {
          // Always load business info (prioritize API data)
          // Only set fields that are relevant to this step
          const relevantData = {
            agent_name: response.agent_name || "AI Bot",
            language: response.language_to_reply || ["Auto Detect"],
            agent_goal_type: response.agent_goal_type,
            additional_agent_goal: response.additional_agent_goal,
            intervention_tools: response.selected_tools || [],
            prompt: response.response_style,
          };

          // Map selected_tools_required_info into dynamic fields for the form
          if (Array.isArray(response.selected_tools_required_info)) {
            const allTools = toolsList || [];
            const byLabel = new Map(allTools.map(t => [t.label, t.issue_type || t.name]));
            const suggestionLabelToName = new Map((requiredInfoSuggestions || []).map(s => [s.label, s.name]));
            response.selected_tools_required_info.forEach((toolInfo) => {
              const keyName = byLabel.get(toolInfo.tool) || toolInfo.tool;
              const key = `tool_required_info_${keyName}`;
              // Map any label-based required_info values to their name keys
              const mappedRequiredInfo = (toolInfo.required_info || []).map(i => suggestionLabelToName.get(i) || i);
              relevantData[key] = mappedRequiredInfo;
              relevantData[`tool_reply_message_${keyName}`] = toolInfo.reply_message || "";
              relevantData[`tool_allow_empty_response_${keyName}`] = toolInfo.allow_empty_response || false;
              relevantData[`tool_block_reply_until_resolved_${keyName}`] = toolInfo.block_reply_until_resolved || false;
            });
          }

          form.setFieldsValue(relevantData);

          // Ensure defaults for any selected tools without required info
          const selected = relevantData.intervention_tools || [];
          selected.forEach((toolKey) => {
            const dynKey = `tool_required_info_${toolKey}`;
            const currentVal = form.getFieldValue(dynKey);
            if (!currentVal || currentVal.length === 0) {
              form.setFieldValue(dynKey, ["name", "contact_number"]);
            }
          });

          // Set primary language (first language in the array is considered primary)
          const languageArray = response.language || [];
          if (languageArray.length > 0 && languageArray[0] !== "Auto Detect") {
            setPrimaryLanguage(languageArray[0]);
          }
          sessionDataLoadedRef.current = true;
        },
        failureCallback: () => { },
      })
    );
  }, [dispatch, form]);

  const updateToolsWithLogic = (selectedTools) => {
    // Get default tools
    const defaultTools = toolsList.filter(t => t.default).map(t => t.issue_type || t.name);
    const nonDefaultTools = selectedTools.filter(t => !defaultTools.includes(t));

    // Only auto-include default tools if there are non-default tools selected
    const allTools = nonDefaultTools.length > 0
      ? [...new Set([...defaultTools, ...selectedTools])]
      : selectedTools;

    // Sort to show default tools first
    const sortedTools = allTools.sort((a, b) => {
      const aIsDefault = toolsList.find(t => (t.issue_type || t.name) === a)?.default;
      const bIsDefault = toolsList.find(t => (t.issue_type || t.name) === b)?.default;
      if (aIsDefault && !bIsDefault) return -1;
      if (!aIsDefault && bIsDefault) return 1;
      return 0;
    });

    // Update form value with sorted tools
    form.setFieldValue('intervention_tools', sortedTools);

    // Clear any dynamic required info fields for deselected tools
    const currentValues = form.getFieldsValue();
    Object.keys(currentValues)
      .filter((k) => k.startsWith('tool_required_info_'))
      .forEach((k) => {
        const toolKey = k.replace('tool_required_info_', '');
        if (!sortedTools.includes(toolKey)) {
          form.setFieldValue(k, []);
          form.setFieldValue(`tool_reply_message_${toolKey}`, "");
          form.setFieldValue(`tool_allow_empty_response_${toolKey}`, false);
          form.setFieldValue(`tool_block_reply_until_resolved_${toolKey}`, false);
        }
      });

    // Set defaults for any selected tools missing values
    sortedTools.forEach((toolKey) => {
      const dynKey = `tool_required_info_${toolKey}`;
      const val = form.getFieldValue(dynKey);
      if (!val || val.length === 0) {
        form.setFieldValue(dynKey, ["name", "contact_number"]);
      }
      if (form.getFieldValue(`tool_allow_empty_response_${toolKey}`) === undefined) {
        form.setFieldValue(`tool_allow_empty_response_${toolKey}`, false);
      }
      if (form.getFieldValue(`tool_block_reply_until_resolved_${toolKey}`) === undefined) {
        form.setFieldValue(`tool_block_reply_until_resolved_${toolKey}`, false);
      }
    });
  };

  const handleToolSelection = (toolId, checked) => {
    const tool = toolsList.find((t) => (t.issue_type || t.name) === toolId);
    if (!tool) return;

    const currentSelected = form.getFieldValue('intervention_tools') || [];

    if (checked) {
      // Selecting
      if (tool.is_pro && !isPaidPlan) {
        setShowUpgradeDialog(true);
        return;
      }

      // Add tool
      const newSelected = [...currentSelected, toolId];
      updateToolsWithLogic(newSelected);
    } else {
      // Deselecting
      if (tool.default) {
        const defaultTools = toolsList.filter(t => t.default).map(t => t.issue_type || t.name);
        const nonDefaultTools = currentSelected.filter(t => !defaultTools.includes(t));

        if (nonDefaultTools.length > 0) {
          messageApi.error('Default tools cannot be removed when other tools are selected');
          return;
        }
      }

      const newSelected = currentSelected.filter(t => t !== toolId);
      updateToolsWithLogic(newSelected);
    }
  };

  const handleSubmit = (values) => {
    if (!validateToolReplyMessages(values)) {
      return;
    }

    // Build tool_required_info array based on selected tools (generic)
    const selectedTools = values.intervention_tools || [];
    const tool_required_info = selectedTools.map((toolName) => {
      const toolKey = getCanonicalToolKey(toolName);

      return {
        tool: toolKey,
        required_info: values[`tool_required_info_${toolKey}`] || values[`tool_required_info_${toolName}`] || [],
        reply_message: form.getFieldValue(`tool_reply_message_${toolKey}`)?.trim() || "We will contact you soon!",
        allow_empty_response: form.getFieldValue(`tool_allow_empty_response_${toolKey}`) ?? false,
        block_reply_until_resolved: form.getFieldValue(`tool_block_reply_until_resolved_${toolKey}`) ?? false,
      };
    });

    // Prepare data for Step 2: AI Agent Setup
    const formData = {
      agent_name: values.agent_name || "AI Bot",
      language: (() => {
        const selectedLanguages = values.language || ["Auto Detect"];
        if (!primaryLanguage || !selectedLanguages.includes(primaryLanguage)) {
          return selectedLanguages;
        }
        // Place primary language first
        const otherLanguages = selectedLanguages.filter(lang => lang !== primaryLanguage);
        return [primaryLanguage, ...otherLanguages];
      })(),
      romanize_reply: values.romanize_reply || false,
      agent_goal_type: values.agent_goal_type,
      additional_agent_goal: values.additional_agent_goal || "",
      prompt: values.prompt || "standard",
      intervention_tools: selectedTools.map((toolName) => getCanonicalToolKey(toolName)),
      selected_tools_required_info: tool_required_info,
    };

    updateStepData("step2", { ...form.getFieldsValue(), ...formData });
    onSubmit(formData);
  };

  const handleValuesChange = (_, allValues) => {
    updateStepData("step2", allValues);
  };

  return (
    <>
      {contextHolder}
      <div className="step2-ai-agent-setup">
        <div className="step2-ai-agent-setup__header">
          <Title
            level={2}
            style={{ marginBottom: 8, color: "#262626", textAlign: "center" }}
          >
            AI Agent Setup
          </Title>
          <Paragraph
            style={{
              textAlign: "center",
              fontSize: "15px",
              color: "#8c8c8c",
              marginBottom: 24,
            }}
          >
            Configure your AI agent's identity and capabilities
          </Paragraph>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleValuesChange}
          disabled={loading}
          initialValues={{
            agent_name: "AI Bot",
            language: ["Auto Detect"],
            prompt: "standard",
            intervention_tools: [],
            ...initialData,
          }}
          size="large"
          requiredMark={false}
        >
          {/* Agent Setup Section */}
          <Card
            title={
              <Space>
                <RobotOutlined style={{ color: "#52c41a", fontSize: 18 }} />
                <Text strong style={{ fontSize: 18 }}>
                  AI Agent Configuration
                </Text>
              </Space>
            }
            className="form-section-card"
            style={{ marginBottom: 24 }}
          >
            <Row gutter={[24, 20]} style={{ padding: 24 }}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="agent_name"
                  label={
                    <span>
                      Agent Name <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please enter the agent name",
                    },
                  ]}
                >
                  <Input
                    prefix={<RobotOutlined style={{ color: "#8c8c8c" }} />}
                    placeholder="Enter agent name"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ marginRight: 8 }}>Language to Reply In</span>
                  {/* <Form.Item name="romanize_reply" valuePropName="checked" noStyle>
                    <Checkbox>
                      Romanize Reply
                      <Tooltip title="Enable this to romanize the AI's responses">
                        <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                      </Tooltip>
                    </Checkbox>
                  </Form.Item> */}
                </div>
                <Form.Item
                  name="language"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one language",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select languages"
                    className="professional-select"
                    value={form.getFieldValue("language") || []}
                    onChange={(selected) => {
                      if (
                        selected.includes("Auto Detect") &&
                        selected.length > 1
                      ) {
                        if (selected[selected.length - 1] === "Auto Detect") {
                          form.setFieldsValue({ language: ["Auto Detect"] });
                          setPrimaryLanguage(null);
                        } else {
                          const filteredSelected = selected.filter((v) => v !== "Auto Detect");
                          form.setFieldsValue({ language: filteredSelected });
                          // If primary was removed, reset it
                          if (!filteredSelected.includes(primaryLanguage)) {
                            setPrimaryLanguage(filteredSelected[0] || null);
                          }
                        }
                      } else {
                        form.setFieldsValue({ language: selected });
                        // If primary was removed, reset it
                        if (!selected.includes(primaryLanguage)) {
                          setPrimaryLanguage(selected[0] || null);
                        }
                      }
                    }}
                    showSearch
                    optionFilterProp="children"
                    dropdownStyle={{ minWidth: 200 }}
                    tagRender={(props) => {
                      const { label, value, closable, onClose } = props;
                      const selectedLanguages = form.getFieldValue("language") || [];
                      const isPrimary = value === primaryLanguage;
                      const showPrimaryOption = selectedLanguages.length > 1 &&
                        value !== "Auto Detect" &&
                        selectedLanguages.includes(value);

                      return (
                        <Tag
                          color={isPrimary ? 'blue' : 'default'}
                          closable={closable}
                          onClose={onClose}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginRight: 4,
                            marginBottom: 4,
                            marginTop: 4,
                          }}
                        >
                          {label}
                          {showPrimaryOption && !isPrimary && (
                            <Tooltip title="The primary language will be used to respond when the user sends a message in a language other than the selected ones.">
                              <Button
                                size="small"
                                type="link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setPrimaryLanguage(value);
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                style={{
                                  marginLeft: 8,
                                  fontSize: 12,
                                  color: '#1890ff',
                                  cursor: 'pointer',
                                }}
                              >
                                Set Primary
                              </Button>
                            </Tooltip>
                          )}
                          {isPrimary && <CheckOutlined style={{ marginLeft: 8 }} />}
                        </Tag>
                      );
                    }}
                  >
                    <OptGroup label="Smart Options">
                      <Option value="Auto Detect">🌐 Auto Detect</Option>
                    </OptGroup>
                    <OptGroup label="Languages">
                      {languages.map((lang) => (
                        <Option key={lang} value={lang}>
                          {lang}
                        </Option>
                      ))}
                    </OptGroup>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="agent_goal_type"
                  label={
                    <span>
                      Agent Goal <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please select an agent goal type",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select agent goal type"
                    className="professional-select"
                  >
                    {agentGoalTypes.map((goal, index) => (
                      <Option key={goal} value={goal}>
                        {goal}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="prompt"
                  label={
                    <span>
                      Response Style <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  rules={[{ required: true, message: "Please select a response style" }]}
                >
                  <Select
                    placeholder="Select response style"
                    optionLabelProp="label"
                    dropdownStyle={{ minWidth: 280 }}
                  >
                    {responseStyles.map((style, index) => (
                      <Option key={style} value={style} label={style}>
                        <div>
                          <div style={{ fontWeight: 500 }}>{style}</div>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                            {style}
                          </div>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name="additional_agent_goal"
                  label="Additional Agent Instruction"
                >
                  <TextArea
                    placeholder="Describe any additional goals or behaviors for your AI agent..."
                    rows={3}
                    className="professional-textarea"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Intervention Tools Section */}
          <Card
            title={
              <Space>
                <RobotOutlined style={{ color: "#1890ff", fontSize: 18 }} />
                <Text strong style={{ fontSize: 18 }}>
                  Follow-up Tools
                </Text>
              </Space>
            }
            className="form-section-card"
            style={{ marginBottom: 24 }}
          >
            <div style={{ padding: 24 }}>
              <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                Select the tools your agent can use to follow up with customers. Configure required information for each enabled tool.
              </Paragraph>

              {/* Hidden field to maintain form state */}
              <Form.Item name="intervention_tools" hidden>
                <Select mode="multiple" />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.intervention_tools !== curr.intervention_tools}
              >
                {({ getFieldValue }) => {
                  const selectedTools = getFieldValue('intervention_tools') || [];

                  // Sort tools for display: Default first, then alphabetical or by original order
                  const sortedDisplayTools = [...toolsList].sort((a, b) => {
                    if (a.default && !b.default) return -1;
                    if (!a.default && b.default) return 1;
                    return 0;
                  });

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {sortedDisplayTools.map((tool) => {
                        const toolId = tool.issue_type || tool.name;
                        const isSelected = selectedTools.includes(toolId);

                        return (
                          <div
                            key={toolId}
                            style={{
                              border: `1px solid ${isSelected ? '#1890ff' : '#d9d9d9'}`,
                              borderRadius: 8,
                              background: isSelected ? '#f0f5ff' : '#fff',
                              transition: 'all 0.3s',
                              overflow: 'hidden'
                            }}
                          >
                            {/* Header */}
                            <div
                              style={{
                                padding: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer'
                              }}
                              onClick={() => handleToolSelection(toolId, !isSelected)}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {(() => {
                                  // Check if this is a default tool that cannot be disabled
                                  const defaultToolIds = toolsList.filter(t => t.default).map(t => t.issue_type || t.name);
                                  const nonDefaultSelectedTools = selectedTools.filter(t => !defaultToolIds.includes(t));
                                  const isDefaultAndLocked = tool.default && isSelected && nonDefaultSelectedTools.length > 0;

                                  const switchElement = (
                                    <Switch
                                      checked={isSelected}
                                      disabled={isDefaultAndLocked}
                                      onClick={(_, e) => e.stopPropagation()}
                                      onChange={(checked) => handleToolSelection(toolId, checked)}
                                    />
                                  );

                                  if (isDefaultAndLocked) {
                                    return (
                                      <Tooltip title="Default tools cannot be disabled while other tools are enabled">
                                        {switchElement}
                                      </Tooltip>
                                    );
                                  }
                                  return switchElement;
                                })()}
                                <div>
                                  <Text strong style={{ fontSize: 16 }}>{tool.label}</Text>
                                  {tool.description && (
                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{tool.description}</div>
                                  )}
                                </div>
                              </div>

                              <Space>
                                {tool.default && (
                                  <Tooltip title="Default Tool">
                                    <Tag color="blue">Default</Tag>
                                  </Tooltip>
                                )}
                                {tool.is_pro && !isPaidPlan && (
                                  <Tag color="gold">PRO</Tag>
                                )}
                              </Space>
                            </div>

                            {/* Expanded Config */}
                            {isSelected && (
                              <div style={{
                                padding: '0 16px 16px 16px',
                                borderTop: '1px solid rgba(0,0,0,0.06)',
                                marginTop: 8,
                                paddingTop: 16,
                                background: '#fff'
                              }}>
                                <Form.Item
                                  name={`tool_required_info_${toolId}`}
                                  label={<span>Required Information for {tool.label}</span>}
                                  style={{ marginBottom: 16 }}
                                >
                                  <Select
                                    mode="tags"
                                    style={{ width: '100%' }}
                                    placeholder={`Select required info for ${tool.label}`}
                                  >
                                    {requiredInfoSuggestions.map((s) => (
                                      <Option key={s.name || s} value={s.name || s}>
                                        {s.label || s}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                                <Row gutter={[16, 12]}>
                                  <Col xs={24} sm={12}>
                                    <Space>
                                      <Form.Item
                                        name={`tool_allow_empty_response_${toolId}`}
                                        valuePropName="checked"
                                        initialValue={false}
                                        noStyle
                                      >
                                        <Switch
                                          size="small"
                                          onChange={(checked) => {
                                            if (checked) {
                                              form.setFields([{ name: `tool_reply_message_${toolId}`, errors: [] }]);
                                            }
                                          }}
                                        />
                                      </Form.Item>
                                      <Text>Allow Empty Response</Text>
                                      <Tooltip title="If enabled, system won't reply to customer after creating this ticket.">
                                        <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                                      </Tooltip>
                                    </Space>
                                  </Col>
                                  <Col xs={24} sm={12}>
                                    <Space>
                                      <Form.Item
                                        name={`tool_block_reply_until_resolved_${toolId}`}
                                        valuePropName="checked"
                                        initialValue={false}
                                        noStyle
                                      >
                                        <Switch size="small" />
                                      </Form.Item>
                                      <Text>Block Reply Until Resolved</Text>
                                      <Tooltip title="If enabled, AI won't reply to the customer until the ticket is resolved.">
                                        <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                                      </Tooltip>
                                    </Space>
                                  </Col>
                                  <Col xs={24}>
                                    <Form.Item
                                      noStyle
                                      shouldUpdate={(prev, curr) =>
                                        prev[`tool_allow_empty_response_${toolId}`] !== curr[`tool_allow_empty_response_${toolId}`]
                                      }
                                    >
                                      {({ getFieldValue }) => {
                                        const allowEmptyResponse = getFieldValue(`tool_allow_empty_response_${toolId}`) || false;

                                        return !allowEmptyResponse ? (
                                          <Form.Item
                                            name={`tool_reply_message_${toolId}`}
                                            label={
                                              <Space>
                                                <span>Reply Message <span style={{ color: "red" }}>*</span></span>
                                                <Tooltip title="This message will be sent to user when allow empty message is disabled.">
                                                  <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                                                </Tooltip>
                                              </Space>
                                            }
                                            style={{ marginBottom: 0, marginTop: 16 }}
                                          >
                                            <TextArea
                                              rows={3}
                                              placeholder={`Enter reply message for ${tool.label}`}
                                            />
                                          </Form.Item>
                                        ) : null;
                                      }}
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              </Form.Item>
            </div>
          </Card>
        </Form>

        <UpgradeDialog
          visible={showUpgradeDialog}
          onClose={() => setShowUpgradeDialog(false)}
          feature="This follow-up tool"
        />
      </div>
    </>
  );
};

export default Step2AIAgentSetup;
